// Termin-Historie Dialog (08/2026): wer hat wann erstellt/verschoben/storniert
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { History, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  created: { label: 'Erstellt', cls: 'bg-blue-100 text-blue-700' },
  rescheduled: { label: 'Verschoben', cls: 'bg-amber-100 text-amber-800' },
  updated: { label: 'Geändert', cls: 'bg-indigo-100 text-indigo-700' },
  cancelled: { label: 'Storniert', cls: 'bg-red-100 text-red-700' },
  participant_added: { label: 'Teilnehmer dazu', cls: 'bg-emerald-100 text-emerald-700' },
  checked_in: { label: 'Eingecheckt', cls: 'bg-green-100 text-green-700' },
};

const fmt = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('de-CH', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(iso).slice(0, 16); }
};

export const AppointmentHistoryDialog = ({ appointmentId, open, onOpenChange }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !appointmentId) return;
    setLoading(true);
    axios.get(`${API}/appointments/${appointmentId}/history`)
      .then((res) => setEntries(res.data.history || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [open, appointmentId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="appointment-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-indigo-600" /> Termin-Historie
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Lade Historie…
          </div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">Keine Historie-Einträge vorhanden.</p>
        ) : (
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
            {entries.map((e, i) => {
              const meta = ACTION_META[e.action] || { label: e.action, cls: 'bg-gray-100 text-gray-700' };
              return (
                <div key={i} className="border border-gray-200 rounded-lg p-3" data-testid={`history-entry-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                    <span className="text-xs text-gray-400">{fmt(e.timestamp)}</span>
                  </div>
                  <p className="mt-1.5 text-sm font-medium text-gray-800">{e.actor}</p>
                  {e.details && <p className="text-xs text-gray-600 mt-0.5">{e.details}</p>}
                  {(e.old_start || e.new_start) && (
                    <p className="text-xs text-gray-500 mt-1">
                      {e.old_start ? `Von: ${fmt(e.old_start)}` : ''}
                      {e.old_start && e.new_start ? ' → ' : ''}
                      {e.new_start ? `Neu: ${fmt(e.new_start)}` : ''}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentHistoryDialog;
